/**
 * לוגיקת הזמנים להזמנות שולחן.
 *
 * הקובץ הזה משוכפל 1:1 בשלושת הפרויקטים (כמו MyDateTime.js):
 *   lola-server/DTechComp/TblOrdTimes.js                               (CommonJS)
 *   lola-admin/src/components/MySections/TimeSections/tblOrdTimes.js   (ES module)
 *   lola-client/src/DTechComp/tblOrdTimes.js                           (ES module - זה הקובץ)
 * כל שינוי כאן חייב להיות מועתק לשני האחרים.
 *
 * מבנה ההגדרות (master.tblOrdSettings):
 *   slotMinutes: 15
 *   weekDays:   [{ dayIndex: 0..6, closed, ranges: [{from,to}], excludedSlots: [] }]
 *   exceptions: [{ title, fromDate:"YYYY-MM-DD", toDate, active, closed, ranges, excludedSlots }]
 *   blockAll:   { active, untilTyp, untilDTS }
 *
 * הערות:
 *  - ב-range, השדה to הוא **השעה האחרונה שניתן להזמין אליה** (כולל).
 *    כלומר 11:00 עד 14:00 מייצר 11:00, 11:15 ... 14:00.
 *  - חריגה דורסת לגמרי את הגדרת יום השבוע לתאריכים שהיא מכסה.
 *  - תאריכי חריגות הם מחרוזות "YYYY-MM-DD" לפי שעון מקומי, כדי לא להיתקל
 *    בהזזת יום בסריאליזציה ל-UTC.
 */

const SLOT_MINUTES = 15;

const DAY_NAMES_HEB = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

const UNTIL_TYPES = [
    { val: "newNotice", txt: "עד להודעה חדשה" },
    { val: "today", txt: "עד סוף היום" },
    { val: "twoDays", txt: "ליומיים הקרובים" },
    { val: "endOfWeek", txt: "עד סוף השבוע הנוכחי" },
    { val: "customDate", txt: "עד תאריך ושעה שאבחר" }
];

const pad2 = (num) => (num < 10 ? "0" + num : "" + num);

/** "HH:MM" -> מספר דקות מחצות. null אם לא תקין */
const toMinutes = (hhmm) => {
    if (typeof hhmm !== "string") { return null; }
    const parts = hhmm.split(":");
    if (parts.length < 2) { return null; }
    const hours = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    if (isNaN(hours) || isNaN(mins)) { return null; }
    return (hours * 60) + mins;
};

/** מספר דקות מחצות -> "HH:MM" */
const toHHMM = (minutes) => pad2(Math.floor(minutes / 60)) + ":" + pad2(minutes % 60);

/** כל משבצות היממה, לשימוש ברשימות הבחירה בצד הניהול */
const allDaySlots = (step) => {
    const stp = (step > 0) ? step : SLOT_MINUTES;
    const bk = [];
    for (let mins = 0; mins < 24 * 60; mins += stp) { bk.push(toHHMM(mins)); }
    return bk;
};

/** המשבצות שבתוך טווח יחיד. to נכלל. מערך ריק אם הטווח לא תקין */
const slotsInRange = (from, to, step) => {
    const stp = (step > 0) ? step : SLOT_MINUTES;
    const start = toMinutes(from);
    const end = toMinutes(to);
    if (start === null || end === null || end < start) { return []; }

    const bk = [];
    for (let mins = start; mins <= end && mins < 24 * 60; mins += stp) { bk.push(toHHMM(mins)); }
    return bk;
};

/** תאריך -> "YYYY-MM-DD" לפי שעון מקומי */
const dayKey = (date) => {
    const dt = (date instanceof Date) ? date : new Date(date);
    if (isNaN(dt.getTime())) { return null; }
    return dt.getFullYear() + "-" + pad2(dt.getMonth() + 1) + "-" + pad2(dt.getDate());
};

/** שלד ריק - כל הימים סגורים, כלומר אין אפשרות להזמין עד שמגדירים */
const defaultSettings = () => {
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
        weekDays.push({ dayIndex: i, closed: true, ranges: [], excludedSlots: [] });
    }
    return {
        slotMinutes: SLOT_MINUTES,
        weekDays,
        exceptions: [],
        blockAll: { active: false, untilTyp: "newNotice", untilDTS: null }
    };
};

const hasSettings = (settings) =>
    !!(settings && Array.isArray(settings.weekDays) && settings.weekDays.length > 0);

const getSlotMinutes = (settings) =>
    (settings && settings.slotMinutes > 0) ? settings.slotMinutes : SLOT_MINUTES;

/** החריגה הפעילה הראשונה שמכסה את התאריך, או null */
const getExceptionForDate = (settings, date) => {
    if (!settings || !Array.isArray(settings.exceptions)) { return null; }
    const key = dayKey(date);
    if (!key) { return null; }

    for (let i = 0; i < settings.exceptions.length; i++) {
        const exc = settings.exceptions[i];
        if (!exc || exc.active === false || !exc.fromDate || !exc.toDate) { continue; }
        //סובלנות למי שהזין את התאריכים הפוך
        const from = (exc.fromDate <= exc.toDate) ? exc.fromDate : exc.toDate;
        const to = (exc.fromDate <= exc.toDate) ? exc.toDate : exc.fromDate;
        if (key >= from && key <= to) { return exc; }
    }
    return null;
};

/** ההגדרה שתקפה לתאריך - חריגה אם יש, אחרת יום השבוע */
const getDayConfig = (settings, date) => {
    if (!hasSettings(settings)) { return null; }

    const exc = getExceptionForDate(settings, date);
    if (exc) { return exc; }

    const dt = (date instanceof Date) ? date : new Date(date);
    if (isNaN(dt.getTime())) { return null; }

    const weekDay = dt.getDay();
    for (let i = 0; i < settings.weekDays.length; i++) {
        if (settings.weekDays[i] && settings.weekDays[i].dayIndex === weekDay) {
            return settings.weekDays[i];
        }
    }
    return null;
};

/** כל המשבצות המוגדרות לתאריך, בלי קשר לשעה הנוכחית ולחסימה הגורפת */
const getSlotsForDate = (settings, date) => {
    const cfg = getDayConfig(settings, date);
    if (!cfg || cfg.closed) { return []; }

    const step = getSlotMinutes(settings);
    const excluded = Array.isArray(cfg.excludedSlots) ? cfg.excludedSlots : [];
    const ranges = Array.isArray(cfg.ranges) ? cfg.ranges : [];
    const bk = [];

    ranges.forEach((rng) => {
        if (!rng) { return; }
        slotsInRange(rng.from, rng.to, step).forEach((slot) => {
            if (excluded.indexOf(slot) === -1 && bk.indexOf(slot) === -1) { bk.push(slot); }
        });
    });

    return bk.sort();
};

/**
 * מצב החסימה הגורפת:
 *   null   - אין חסימה (או שהיא כבר פגה)
 *   "all"  - הכל חסום, עד להודעה חדשה
 *   Date   - חסום עד המועד הזה
 */
const getBlockAllUntil = (settings, now) => {
    const nw = now || new Date();
    const blk = settings && settings.blockAll;
    if (!blk || !blk.active) { return null; }
    if (blk.untilTyp === "newNotice" || !blk.untilDTS) { return "all"; }

    const until = new Date(blk.untilDTS);
    if (isNaN(until.getTime())) { return "all"; }
    if (until <= nw) { return null; }
    return until;
};

const isBlockAllActive = (settings, now) => getBlockAllUntil(settings, now) !== null;

/** תאריך + "HH:MM" -> אובייקט Date מלא */
const slotDateTime = (date, slot) => {
    const dt = (date instanceof Date) ? new Date(date.getTime()) : new Date(date);
    const mins = toMinutes(slot);
    if (isNaN(dt.getTime()) || mins === null) { return null; }
    dt.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
    return dt;
};

/** המשבצות שבאמת ניתן להזמין אליהן בתאריך הזה */
const getAvailableSlots = (settings, date, now) => {
    const nw = now || new Date();
    const blockUntil = getBlockAllUntil(settings, nw);
    if (blockUntil === "all") { return []; }

    return getSlotsForDate(settings, date).filter((slot) => {
        const dts = slotDateTime(date, slot);
        if (!dts) { return false; }
        if (dts <= nw) { return false; }
        if (blockUntil && dts <= blockUntil) { return false; }
        return true;
    });
};

const isDateAvailable = (settings, date, now) => getAvailableSlots(settings, date, now).length > 0;

/** התאריך הזמין הקרוב ביותר, או null אם אין כזה בטווח החיפוש */
const findFirstAvailableDate = (settings, now, maxDays) => {
    const nw = now || new Date();
    const limit = (maxDays > 0) ? maxDays : 60;

    for (let i = 0; i < limit; i++) {
        const dt = new Date(nw.getFullYear(), nw.getMonth(), nw.getDate() + i);
        if (getAvailableSlots(settings, dt, nw).length > 0) { return dt; }
    }
    return null;
};

const endOfDay = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

/** מתרגם את בחירת ה"עד מתי" למועד ממשי, כדי שהחסימה תפוג לבד */
const computeBlockUntil = (untilTyp, now, customDTS) => {
    const nw = now || new Date();

    if (untilTyp === "today") {
        return endOfDay(nw);
    }
    if (untilTyp === "twoDays") {
        return endOfDay(new Date(nw.getFullYear(), nw.getMonth(), nw.getDate() + 1));
    }
    if (untilTyp === "endOfWeek") {
        //שבוע ישראלי: ראשון (0) עד שבת (6)
        return endOfDay(new Date(nw.getFullYear(), nw.getMonth(), nw.getDate() + (6 - nw.getDay())));
    }
    if (untilTyp === "customDate") {
        const custom = customDTS ? new Date(customDTS) : null;
        return (custom && !isNaN(custom.getTime())) ? custom : null;
    }
    return null; //newNotice
};

const tblOrdTimes = {
    SLOT_MINUTES,
    DAY_NAMES_HEB,
    UNTIL_TYPES,
    toMinutes,
    toHHMM,
    allDaySlots,
    slotsInRange,
    dayKey,
    defaultSettings,
    hasSettings,
    getSlotMinutes,
    getExceptionForDate,
    getDayConfig,
    getSlotsForDate,
    getBlockAllUntil,
    isBlockAllActive,
    slotDateTime,
    getAvailableSlots,
    isDateAvailable,
    findFirstAvailableDate,
    computeBlockUntil
};

export default tblOrdTimes;
